import {
  useEffect,
  useRef,
} from 'react';

import { CheckMark } from '@/core/components/CheckMark';
import { NextSteps } from '@/core/interfaces/common';

import {
  CheckMarkWrapper,
  DonateLink,
  DonateLinkWrapper,
  ShareIconsWrapper,
  ThankYouHeadline,
  WantMoreText,
  Wrapper,
} from './ThankYouWrapper.styles';
import { ShareIcons } from '../ShareIcons';

interface ThankYouWrapperProps {
  borderColor: string;
  buttonColor: string;
  children: React.ReactNode;
  isFullScreen?: boolean;
  name: string;
  nextSteps: NextSteps | { [key: string]: never };
  socialShareButtonEnabled: boolean;
  textcolor: string;
  uuid?: string | Array<string>;
  shouldFocus?: boolean;
}

export const ThankYouWrapper = ({
  borderColor,
  buttonColor,
  children,
  isFullScreen,
  name,
  nextSteps,
  socialShareButtonEnabled,
  textcolor,
  uuid,
  shouldFocus = false,
}: ThankYouWrapperProps) => {
  const headlineRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if ('shouldRedirect' in nextSteps && nextSteps.shouldRedirect && nextSteps.url) {
      const url = nextSteps.url.startsWith('http://') || nextSteps.url.startsWith('https://') ?
        nextSteps.url :
        `https://${nextSteps.url}`;

      if (window.top) {
        // When embedded in a cross-origin iframe, the browser only permits
        // setting href (or calling replace()) on the parent page's Location;
        // reading any other property, including assign, throws a
        // SecurityError. Setting href still adds a history entry, so the
        // back button keeps working.
        window.top.location.href = url;
      } else {
        window.location.assign(url);
      }
    }
  }, [nextSteps]);

  useEffect(() => {
    if (shouldFocus && headlineRef.current) {
      // Scroll the heading into view
      if (headlineRef.current.scrollIntoView) {
        headlineRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      // Programmatically set focus to the heading for screen readers
      if (headlineRef.current.focus) {
        headlineRef.current.focus();
      }
    }
  }, [shouldFocus]);

  return (
    <Wrapper
      $borderColor={borderColor}
      $isFullScreen={isFullScreen}
    >
      <CheckMarkWrapper>
        <CheckMark backgroundColor={textcolor} />
      </CheckMarkWrapper>
      <ThankYouHeadline
        ref={headlineRef}
        tabIndex={-1}
      >
        {name}
      </ThankYouHeadline>
      {children}
      {'url' in nextSteps && nextSteps.url && !nextSteps.shouldRedirect && (
        <DonateLinkWrapper>
          <DonateLink
            backgroundcolor={buttonColor}
            href={nextSteps.url.startsWith('http://') || nextSteps.url.startsWith('https://') ? nextSteps.url : `https://${nextSteps.url}`}
            rel="noreferrer"
            textcolor={textcolor}
            target="_blank"
          >
            {nextSteps.buttonText}
          </DonateLink>
        </DonateLinkWrapper>
      )}
      {socialShareButtonEnabled && typeof uuid === 'string' && (
        <>
          <WantMoreText>Take one more step and share with your friends</WantMoreText>
          <ShareIconsWrapper>
            <ShareIcons
              textcolor={buttonColor}
              customIcons
              uuid={uuid}
            />
          </ShareIconsWrapper>
        </>
      )}
    </Wrapper>
  );
};

ThankYouWrapper.defaultProps = {
  uuid: null,
};
